export type SkillTemplate = {
  name: string;
  description: string;
  version: string;
  render: () => string;
  /**
   * Renderers for past versions of this skill, keyed by semver.
   * When bumping `version` + `render`, move the previous render fn in here
   * under its old version string so `canary skill diff` can reproduce it.
   */
  previousVersions?: Record<string, () => string>;
};

export const issueLogXrefTemplate: SkillTemplate = {
  name: "issue-log-xref",
  description:
    "Cross-reference Canary issues with server logs to find root causes",
  version: "1.0.0",
  render: () => `---
name: issue-log-xref
version: 1.0.0
description: Cross-reference Canary QA issues with server logs to identify root causes. Use when investigating why a test failed, correlating browser errors with backend logs, or debugging issues found by Canary.
allowed-tools: Bash(canary issues:*), Bash(canary login:*), Grep, Read
---

# Issue Log Cross-Reference

Cross-reference Canary-detected issues with server logs to identify root causes, correlate errors, and provide actionable debugging context.

## Prerequisites

- \`canary\` CLI installed and authenticated (\`canary login\`)
- Access to server logs (see Log Source Configuration below)

## Workflow

### 1. Fetch issues from Canary

List recent issues to find ones worth investigating:

\`\`\`bash
canary issues list --format markdown
\`\`\`

Use filters to narrow down:

\`\`\`bash
canary issues list --severity high --status open --format markdown
\`\`\`

### 2. Get issue details

Once you identify an issue, fetch its full diagnostics:

\`\`\`bash
canary issues get <issue-id> --format markdown
\`\`\`

Note the following from the issue details:
- **Timestamps** — when the issue was detected
- **URLs / endpoints** — which pages or API routes were involved
- **Error messages** — any visible error text captured in screenshots or DOM
- **HTTP status codes** — failed network requests

### 3. Access server logs

<!-- ============================================================
  LOG SOURCE CONFIGURATION

  Replace {{LOG_SOURCE_INSTRUCTIONS}} below with instructions
  specific to your infrastructure. The AI agent reading this
  skill will use these instructions to fetch logs.

  Examples:

  **AWS CloudWatch:**
  Query CloudWatch Logs Insights for the relevant log group:
  \`\`\`bash
  aws logs filter-log-events \\
    --log-group-name /ecs/my-api \\
    --start-time <epoch-ms> \\
    --end-time <epoch-ms> \\
    --filter-pattern "<error-text>"
  \`\`\`

  **Local log files:**
  Search local log files for matching entries:
  \`\`\`bash
  grep -i "<error-text>" /var/log/my-app/*.log
  \`\`\`

  **Datadog:**
  Use the Datadog CLI or API to query logs:
  \`\`\`bash
  dog logs list --query "service:my-api status:error" \\
    --from <ISO-timestamp> --to <ISO-timestamp>
  \`\`\`

  **kubectl (Kubernetes):**
  Fetch pod logs for the relevant service:
  \`\`\`bash
  kubectl logs -l app=my-api --since=1h | grep "<error-text>"
  \`\`\`
============================================================ -->

{{LOG_SOURCE_INSTRUCTIONS}}

### 4. Cross-reference and correlate

Match the Canary issue with log entries using these correlation points:

- **Timestamps** — find log entries within a few seconds of the issue detection time
- **Error messages** — search logs for the same error text shown in the issue
- **HTTP status codes** — look for 4xx/5xx responses matching the failed requests
- **URLs / routes** — filter logs by the endpoint or page path from the issue
- **Stack traces** — identify the code path that produced the error
- **Request IDs** — if your app uses correlation/request IDs, trace the full request lifecycle

## Analysis Guidelines

When presenting findings:

1. **State the match confidence** — clear match, likely match, or circumstantial
2. **Show the timeline** — lay out events in chronological order across both sources
3. **Identify the root cause** — distinguish between the symptom (what Canary caught) and the cause (what the logs reveal)
4. **Suggest next steps** — point to the specific code, service, or configuration that needs attention
5. **Note gaps** — if logs are missing or incomplete, say so explicitly rather than speculating
`,
};
